/**
 * HelpView — the full FAQ / user guide. Content comes from the backend
 * (/api/beacon/luma/faq) — the same source Luma answers help questions
 * from, so chat and page never drift apart.
 */
import { useEffect, useState } from "react";
import axios from "axios";

import { API } from "../lib/beaconUtils";

function FaqItem({ item, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`beacon-faq-item${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="beacon-faq-q"
        data-testid={`faq-q-${id}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{item.q}</span>
        <span className="beacon-faq-chev" aria-hidden="true">
          {open ? "–" : "+"}
        </span>
      </button>
      {open && (
        <div className="beacon-faq-a" data-testid={`faq-a-${id}`}>
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function HelpView({ onAskLuma }) {
  const [sections, setSections] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/luma/faq`)
      .then((r) => setSections(r.data.sections))
      .catch((e) => setError(e.message));
  }, []);

  return (
    <section className="beacon-help" data-testid="help-view">
      <div className="beacon-plan-header">
        <div>
          <div className="beacon-card-eyebrow">HELP · FAQ</div>
          <h2 className="beacon-card-title">How Beacon works.</h2>
          <p className="beacon-card-lede">
            Everything your crew of one needs to know — Luma, review
            scores, rebuilds, jurisdictions and the weekly rhythm. Can&rsquo;t
            find it here? Ask Luma — it answers from this exact guide.
          </p>
        </div>
        <div className="beacon-help-actions">
          <button type="button" className="beacon-secondary-btn"
            data-testid="help-replay-tour"
            onClick={() => window.dispatchEvent(new Event("beacon-start-tour"))}>
            ↺ Replay the tour
          </button>
          {onAskLuma && (
            <button type="button" className="beacon-secondary-btn"
              data-testid="help-ask-luma" onClick={onAskLuma}>
              ✦ Ask Luma instead
            </button>
          )}
        </div>
      </div>

      {error && <div className="beacon-tab-error">Couldn&rsquo;t load the FAQ: {error}</div>}
      {!sections && !error && (
        <div className="beacon-tab-loading">
          <span className="beacon-spinner beacon-spinner-dark" />Loading guide…
        </div>
      )}

      {sections && (
        <>
          <div className="beacon-faq-toc" data-testid="help-toc">
            {sections.map((s) => (
              <a key={s.key} href={`#faq-${s.key}`}
                className="beacon-faq-toc-link"
                data-testid={`help-toc-${s.key}`}>
                {s.title}
              </a>
            ))}
          </div>

          {sections.map((s) => (
            <div key={s.key} id={`faq-${s.key}`} className="beacon-faq-section"
              data-testid={`help-section-${s.key}`}>
              <h3 className="beacon-faq-section-title">{s.title}</h3>
              {s.items.map((item, i) => (
                <FaqItem key={i} item={item} id={`${s.key}-${i}`} />
              ))}
            </div>
          ))}
        </>
      )}
    </section>
  );
}
